import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDecideValidation, useMyValidations } from '../../api/validations.js';
import type { Validation } from '../../api/types.js';
import { formatRelative } from '../invoices/format.js';
import { DecideModal } from './DecideModal.js';
import { PageWrapper } from '../../shell/PageWrapper.js';

interface Props {
  tenantId: string;
  tenantName: string;
}

export function ValidationsScreen({ tenantId, tenantName }: Props) {
  const query = useMyValidations('pending');
  const decideMutation = useDecideValidation();
  const [active, setActive] = useState<Validation | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function handleDecide(payload: { decision: 'accept' | 'refuse'; comment: string | null }) {
    if (!active) return;
    setToast(null);
    try {
      await decideMutation.mutateAsync({
        validationId: active.validationId,
        payload,
      });
      setActive(null);
      setToast(payload.decision === 'accept' ? 'Validation acceptée.' : 'Validation refusée.');
    } catch {
      setToast('Échec de la décision.');
    }
  }

  // Filter to this tenant (BE returns all of caller's pending across tenants;
  // the route is per-tenant so we surface only the relevant ones here).
  const items = (query.data ?? []).filter((v) => !tenantId || v.tenantId === tenantId);

  return (
    <PageWrapper>
      <section className="invoices" aria-label="Validations à faire">
        <header className="invoices__head">
          <div>
            <div className="eyebrow">{tenantName}</div>
            <h1 className="display invoices__title">Validations à faire</h1>
          </div>
        </header>

        {query.isLoading ? (
          <div className="invoices__table" data-testid="validations-loading">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="invoice-row invoice-row--skeleton">
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            ))}
          </div>
        ) : query.isError ? (
          <div className="invoices__error" role="alert">
            Impossible de charger les validations.
          </div>
        ) : items.length === 0 ? (
          <div className="invoices__empty" data-testid="validations-empty">
            <p className="invoices__empty-title">Aucune validation en attente.</p>
          </div>
        ) : (
          <ul className="validations" data-testid="validations-list">
            {items.map((v) => (
              <li
                key={v.validationId}
                className="validation-row"
                data-testid={`validation-${v.validationId}`}
              >
                <button type="button" className="validation-row__btn" onClick={() => setActive(v)}>
                  <div className="validation-row__main">
                    <span className="pill pill--info">en attente</span>
                    <Link
                      to={`/t/${v.tenantId}/invoices/${encodeURIComponent(v.primaryKey)}`}
                      className="validation-row__pk"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {v.primaryKey.split('__').slice(-1)[0]}
                    </Link>
                  </div>
                  <div className="validation-row__requester muted">
                    Demandé par {v.requesterDisplayName ?? v.requesterEmail ?? v.requesterUid}
                    {' · '}
                    {formatRelative(v.requestedAt)}
                  </div>
                  {v.requestComment ? (
                    <div className="validation-row__comment">« {v.requestComment} »</div>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}

        {active ? (
          <DecideModal
            validation={active}
            busy={decideMutation.isPending}
            onDecide={handleDecide}
            onClose={() => setActive(null)}
          />
        ) : null}

        {toast ? (
          <div className="invoice-detail__toast" role="status" data-testid="decide-toast">
            {toast}
          </div>
        ) : null}
      </section>
    </PageWrapper>
  );
}
